import moment from 'moment-timezone';
import { range } from 'lodash';

const moneyFormatter = range(0, 4).map(
    (i) =>
        new Intl.NumberFormat('de-DE', {
            style: 'currency',
            minimumFractionDigits: i,
            maximumFractionDigits: i,
            currency: 'EUR',
        }),
);

const percentFormatter = range(0, 4).map(
    (i) =>
        new Intl.NumberFormat('de-DE', {
            style: 'percent',
            minimumFractionDigits: i,
            maximumFractionDigits: i,
        }),
);

const decimalFormatter = range(0, 6).map(
    (i) =>
        new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: i,
            maximumFractionDigits: i,
        }),
);

export function getDecimalCount(value: number | undefined | null): number {
    if (!value || Math.floor(value) === value) {
        return 0;
    }

    return value.toString().split('.')[1].length || 0;
}

export function formatDecimal(value: number, decimals = 2): string {
    if (value === undefined || value === null) {
        return '-';
    }

    return decimalFormatter[Math.min(decimals, 5)].format(value);
}

export function parseDecimal(value: string | null | undefined): number | null {
    if (value === undefined || value === null) {
        return null;
    }

    return parseFloat((value || '').replace('.', '').replace(',', '.')) || null;
}

export function formatPercent(value: number, decimals = 0): string {
    if (value === undefined || value === null) {
        return '-';
    }

    return percentFormatter[decimals].format(value / 100);
}

export function formatMoney(value: number, decimals = 2): string {
    if (value === undefined || value === null) {
        return '-';
    }

    return moneyFormatter[decimals].format(value);
}

export const defaultSmDate = '1753-01-01T00:00:00';

export function formatSmDate(date?: string, emptyIfNotSet = false): string {
    if (!date || date === defaultSmDate) {
        return emptyIfNotSet ? '' : '??.??.????';
    }

    return moment(date).format('DD.MM.YYYY');
}

export function formatDecimalTime(decimalTime?: number, emptyIfNotSet = false): string {
    if (!decimalTime) {
        return emptyIfNotSet ? '' : '??:??';
    }

    const s = decimalTime.toFixed(2).replace('.', ':');

    return decimalTime < 10 ? '0' + s : s;
}

export function formatSmDateTime(date?: string, decimalTime?: number, emptyIfNotSet = false): string {
    return formatSmDate(date, emptyIfNotSet) + ' ' + formatDecimalTime(decimalTime, emptyIfNotSet);
}

export function encodeRecordNr(recordNr: string, replaceSlash = true): string {
    if (!recordNr) {
        return '';
    }
    return encodeURIComponent(replaceSlash ? recordNr.replace('/', '-') : recordNr);
}

export function decodeRecordNr(encodedRecordNr: string, replaceSlash = true): string {
    return replaceSlash
        ? decodeURIComponent((encodedRecordNr || '').replace('-', '/'))
        : decodeURIComponent(encodedRecordNr || '');
}

export function createKundeUrl(kundeNr?: number | string): string {
    return '/kunden/' + kundeNr;
}

export function createArtikelDetailUrl(artikelGruppeNr: number, artikelNr: string): string {
    return '/lager/artikel/' + encodeURIComponent(artikelGruppeNr + '|' + artikelNr);
}

export type ViewType =
    | 'kunden'
    | 'taetigkeiten'
    | 'wiedervorlagen'
    | 'angebote'
    | 'services'
    | 'auftraege'
    | 'lieferungen'
    | 'rechnungen'
    | 'lieferantenrechnungen'
    | 'bestellungen'
    | 'lager/artikel';

export function createRecordUrl(
    view: ViewType | null,
    kundeNr?: string | number,
    recordNr?: string,
    subPage?: string,
    replaceSlash = true,
): string {
    if (recordNr) {
        if (kundeNr) {
            return '/kunden/' + kundeNr + '/' + view + '/' + encodeRecordNr(recordNr || '', replaceSlash);
        }
        return '/' + view + '/' + encodeRecordNr(recordNr || '', replaceSlash) + (subPage ? '/' + subPage : '');
    }

    if (kundeNr) {
        return '/kunden/' + kundeNr + '/' + (view || '');
    }
    return '/' + view;
}

export type ReportType = 'angebotsuebersicht' | 'auftragsuebersicht' | 'rechnungsuebersicht' | 'umsatzuebersicht';

export function createReportUrl(report: ReportType, kNr?: number): string {
    return '/berichte/' + report + (kNr ? '?kNr=' + kNr : '');
}
